/**
 * ROUTE PARAM ENFORCEMENT UTILITY
 * 
 * Enforces param correctness at runtime. All entity detail pages MUST call requireParam
 * for each URL parameter to prevent silent failures when params are undefined.
 * 
 * Contract:
 * - Case routes use { caseId }
 * - Draft routes use { caseId, draftId }
 * - Filing routes use { caseId, filingId }
 * - Evidence routes use { caseId, evidenceId }
 * 
 * @throws Error with descriptive message if param is missing
 */

export function requireParam(
  name: string,
  value: string | undefined
): asserts value is string {
  if (!value) {
    const error = new Error(
      `[ROUTE PARAM VIOLATION] Missing required URL param: "${name}". ` +
      `This indicates a routing misconfiguration. Check that: ` +
      `1) The route is properly defined with :${name} in router.tsx, ` +
      `2) useParams() is called in the component, ` +
      `3) The param name matches exactly (case-sensitive)`
    );
    
    // In development, log loudly
    if (import.meta.env.DEV) {
      console.error(error.message);
      console.error('Current URL:', window.location.pathname);
      console.error('Expected param:', name);
    }
    
    throw error;
  }
}

/**
 * Convenience function for case pages that require caseId
 */
export function requireCaseId(caseId: string | undefined): asserts caseId is string {
  requireParam('caseId', caseId);
}

/**
 * Convenience function for draft pages
 */
export function requireDraftParams(
  caseId: string | undefined,
  draftId: string | undefined
): asserts caseId is string {
  requireParam('caseId', caseId);
  requireParam('draftId', draftId);
}

/**
 * Convenience function for filing pages
 */
export function requireFilingParams(
  caseId: string | undefined,
  filingId: string | undefined
): asserts caseId is string {
  requireParam('caseId', caseId);
  requireParam('filingId', filingId);
}

/**
 * Convenience function for evidence pages
 */
export function requireEvidenceParams(
  caseId: string | undefined,
  evidenceId: string | undefined
): asserts caseId is string {
  requireParam('caseId', caseId);
  requireParam('evidenceId', evidenceId);
}
